<template>
  <div class="modal-bg">
    <div class="wrapper">
      <div class="header">
        <img :src="logoUrl" class="routine-logo" />
        <span class="email">{{ email }}</span>
      </div>

      <div class="main">
        <template v-if="client">
          <img
            v-if="client.icon"
            :src="client.icon"
            :alt="client.name"
            class="oauth-logo"
          />
          <div v-else class="logo-placeholder">
            <span class="logo-letter">{{ client.name[0] }}</span>
          </div>

          <div class="title">
            <a class="home-link" :href="client.homepage" target="_blank">{{
              client.name
            }}</a
            >wants to access Routine to:
          </div>
          <div class="capacities">
            <div v-for="cap in capacities" :key="cap.id" class="capacity">
              {{ cap.text }}
            </div>
          </div>

          <div class="buttons">
            <BaseButton kind="auxiliary" text="Cancel" @click="goToRoot" />
            <BaseButton
              kind="main"
              type="primary"
              text="Allow"
              @click="authorize"
            />
          </div>
        </template>
        <div v-else-if="error" class="error">
          <span class="error-title">Something has gone wrong.</span>
          <BaseButton
            kind="main"
            type="primary"
            tint="red"
            text="Go back"
            @click="goToRoot"
          />
        </div>
        <Spinner v-else class="spinner" />
      </div>

      <div v-if="client" class="footer">
        <div class="footer-title">
          Make sure you trust {{ client.name }} (
          <a :href="client.homepage" class="client-link link" target="_blank">
            {{ client.homepage }}
          </a>
          ).
        </div>

        <div class="footer-description">
          If you continue, you may be sharing sensitive information. Routine
          does not review third party integrations such as {{ client.name }}.
          Learn how {{ client.name }} handles your data:
        </div>

        <div class="links">
          <a :href="client.privacy" class="link" target="_blank">
            Privacy Policy
          </a>
          -
          <a :href="client.tou" class="link" target="_blank"> Terms of Use </a>
          - {{ client.email }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { startOfToday } from "date-fns";
import { computed, defineComponent, ref } from "vue";
import { useRoute } from "vue-router";

import { Capacity, ClientGrant } from "@ctrl/controller-rpc";

import assert from "@/assert";
import BaseButton from "@/components/base/BaseButton.vue";
import * as inject from "@/components/inject";
import { useReleaser } from "@/ref";
import { in_electron } from "@/utils/in";
import { navigateToAgenda } from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";

export default defineComponent({
  name: "OAuth",
  components: {
    BaseButton,
  },
  async setup() {
    const controller = inject.controller();
    const route = useRoute();
    const router = inject.router();
    // Route query parameters
    const clientId = route?.query["client_id"];
    const redirectUri = route?.query["redirect_uri"];
    const responseType = route?.query["response_type"];
    const state = route?.query["state"];
    const error = ref<string | null>(null);
    const client = ref<ClientGrant | null>(null);
    assert(typeof clientId == "string");
    assert(typeof redirectUri == "string");
    assert(typeof responseType == "string");
    const releaser = useReleaser();
    const identities = await releaser.add(controller.auth.profiles());
    const email = computed<string>(() => identities.value[0].email);
    const getClient = async (id: string) => {
      try {
        client.value = await controller.oauth.getClient(id);
      } catch (e) {
        error.value = e instanceof Error ? e.message : String(e);
      }
    };
    getClient(clientId);
    const capacities = computed((): { text: string; id: Capacity }[] => {
      if (!client.value) return [];
      const { grant } = client.value;
      if (!grant) return [{ text: "Add tasks to your Inbox", id: "task_add" }];
      return grant.capacities.map((c: Capacity) => {
        if (c == "task_add") return { text: "Add tasks to your Inbox", id: c };
        else if (c === "task_archive") return { text: "Complete tasks", id: c };
        else {
          const exhaustiveCheck: never = c;
          throw new Error(`Unhandled capacity type: ${exhaustiveCheck}`);
        }
      });
    });
    const logoUrl = computed(() => {
      return new URL(`../assets/logo-icon.svg`, import.meta.url).href;
    });
    const authorize = async () => {
      try {
        const uri = await controller.oauth.authorize(
          clientId,
          redirectUri,
          responseType,
          typeof state == "string" ? state : null
        );
        if (in_electron) {
          openExternalUrl(uri, controller);
          goToRoot();
        } else {
          window.location.assign(uri);
        }
      } catch (e) {
        error.value = e instanceof Error ? e.message : String(e);
      }
    };
    const goToRoot = () => {
      navigateToAgenda(router, startOfToday());
    };
    return {
      authorize,
      capacities,
      client,
      email,
      error,
      goToRoot,
      logoUrl,
    };
  },
});
</script>

<style scoped lang="scss">
.modal-bg {
  display: flex;

  .wrapper {
    display: flex;
    flex-direction: column;
    margin: auto;
    width: 510px;
    border-radius: 5px;
    overflow: hidden;
    @include themed {
      background-color: t($bg);
    }

    .header {
      display: flex;
      padding: 12px 24px;
      justify-content: space-between;
      align-items: center;
      @include themed {
        border-bottom: 1px solid t($grey-15);
      }

      .routine-logo {
        width: 20px;
      }

      .email {
        @extend %font-small-regular;
        @include themed {
          color: t($grey-50);
        }
      }
    }

    .main {
      padding: 26px 0 32px;
      display: flex;
      flex-direction: column;

      .oauth-logo {
        width: 56px;
        height: 56px;
        margin: 0 auto 12px;
      }

      .logo-placeholder {
        margin: 0 auto 12px;
        display: flex;
        border-radius: 60px;
        width: 56px;
        height: 56px;
        @include themed {
          background-color: t($secondary-10);
        }

        .logo-letter {
          margin: auto;
          font-size: 18px;
          line-height: 32px;
          @include themed {
            color: t($secondary-40);
          }
        }
      }

      .title {
        font-size: 18px;
        line-height: 32px;
        margin-bottom: 24px;
        text-align: center;

        .home-link {
          text-decoration: underline;
          margin-inline-end: 4px;
          @include themed {
            color: t($grey-50);
          }
        }
      }

      .capacities {
        display: flex;
        flex-direction: column;
        margin: 0 auto;
        text-align: center;
        column-gap: 4px;

        .capacity {
          margin-inline-start: 12px;
          font-weight: 500;
          font-size: 15px;
          line-height: 24px;

          &:before {
            content: "";
            display: inline-block;
            width: 4px;
            height: 4px;
            border-radius: 50px;
            margin-bottom: 3px;
            margin-inline-end: 8px;
            @include themed {
              background-color: t($grey-80);
            }
          }

          &:not(:last-child) {
            margin-bottom: 4px;
          }
        }
      }

      .buttons {
        display: flex;
        margin: 32px auto 0;
        gap: 12px;

        * {
          margin-top: auto;
          margin-bottom: auto;
        }
      }
    }

    .error {
      display: flex;
      flex-direction: column;

      .error-title {
        margin: 32px auto;
        @extend %font-body-medium;
        @include themed {
          color: t($secondary);
        }
      }
    }

    .spinner {
      margin: 32px auto;
    }

    .footer {
      padding: 24px 32px;
      display: flex;
      flex-direction: column;
      @include themed {
        background-color: t($grey-03);
        border-top: 1px solid t($grey-15);
      }

      .footer-title {
        @extend %font-caption-regular;
        margin-bottom: 8px;
      }

      .client-link {
        margin-inline-start: -2px;
        margin-inline-end: -2px;
      }

      .footer-paragaraph {
        @extend %font-small-regular;
      }

      .footer-description {
        @extend %font-small-regular;
        margin: 8px 0;
        @include themed {
          color: t($grey-50);
        }
      }

      .links {
        @extend %font-caption-medium;
        @include themed {
          color: t($grey-50);
        }
      }
    }
  }

  .link {
    @extend %font-caption-medium;
    text-decoration: none;
    @include themed {
      color: t($tertiary);
    }
  }
}
</style>
